import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { CalendarRange, Boxes, Radio } from 'lucide-react';
import dayjs from 'dayjs';
import { queryKeys } from '@/lib/api/queryKeys';
import type { XkEvent } from '@/lib/api/types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { useT } from '@/lib/i18n/useT';
import { eventPhase } from '../eventPhase';
import { ProjectAtlediInsight } from '../components/ProjectAtlediInsight';
import { factsApi } from '@/features/insights/factsApi';
import {
  aggregateFacts,
  bigOf,
  env,
  fmtInt,
  fmtPct,
  leadRateOf,
  phaseOf,
} from '@/features/insights/engine';
import { KpiCard, MiniArc, MiniBars, MiniDots, MiniSplit } from '@/features/insights/charts/MiniViz';
import { EVENT_COLORS, SEGMENT_COLORS, TRACK_COLOR } from '@/features/insights/charts/chartTokens';
import { ProjectThreePanels } from '../components/ProjectThreePanels';
import { CapacityIndicator } from '../components/CapacityIndicator';
import { SurveyDemographics } from '@/features/demographics/SurveyDemographics';

/**
 * MEASURE tab — project-wide statistics: summary counts, the Atledi insight,
 * the three parallel panels, capacity, and a stock-allocation chart sourced
 * from the existing group report endpoint.
 */
export function MeasureTab({
  groupId,
  brandId,
  projectName,
  events,
  startDate,
  endDate,
}: {
  groupId: number;
  brandId: number;
  projectName: string;
  events: XkEvent[];
  startDate: string | null;
  endDate: string | null;
}) {
  const t = useT();

  // Los hechos de todos los eventos del proyecto: de aquí salen las tarjetas
  // KPI, las capturas por evento y lo desplegado por artículo. Es el mismo
  // origen que el informe de cada evento, así que las sumas cuadran.
  const eventIds = useMemo(() => events.map((e) => e.id).sort((a, b) => a - b), [events]);
  const factsQ = useQuery({
    queryKey: queryKeys.insights.facts(eventIds),
    queryFn: () => factsApi.forEvents(eventIds),
    enabled: eventIds.length > 0,
    staleTime: 30_000,
  });
  const facts = useMemo(() => factsQ.data?.events ?? [], [factsQ.data]);
  const agg = useMemo(() => aggregateFacts(facts), [facts]);
  const uniqueContacts = factsQ.data?.uniqueContacts ?? 0;
  const repeatContacts = factsQ.data?.repeatContacts ?? 0;
  const repeatRate = env(repeatContacts, uniqueContacts, 'contacts');

  const liveCount = events.filter((e) => eventPhase(e) === 'live').length;
  const rangeLabel =
    startDate && endDate
      ? `${dayjs(startDate).format('MMM D')} – ${dayjs(endDate).format('MMM D, YYYY')}`
      : t('project.tbd');

  // Una barra por captura (evento con hechos), en orden de fecha.
  const captures = useMemo(
    () =>
      [...facts]
        .sort((a, b) => (a.event.startDate ?? '').localeCompare(b.event.startDate ?? ''))
        .map((f) => ({
          id: f.event.id,
          name: f.event.name,
          contacts: f.funnel.contacts,
          leads: f.funnel.leads,
          delivered: f.funnel.prizesDelivered,
          phase: phaseOf(f),
          leadRate: bigOf(leadRateOf(f), '—'),
        })),
    [facts],
  );
  const withData = captures.filter((c) => c.contacts > 0 || c.delivered > 0);
  const single = withData.length < 2;
  const paceGoal = agg.leadGoal;
  const doneLeads = facts.filter((f) => phaseOf(f) === 'done').reduce((a, f) => a + f.funnel.leads, 0);
  const liveLeads = facts.filter((f) => phaseOf(f) === 'live').reduce((a, f) => a + f.funnel.leads, 0);
  const pace = env(doneLeads, paceGoal, 'leadGoal');
  const accounted = agg.ledger.consumed - agg.ledger.unaccounted;
  const unaccPct = agg.ledger.consumed ? (agg.ledger.unaccounted / agg.ledger.consumed) * 100 : 0;
  const repeatPct = uniqueContacts ? (repeatContacts / uniqueContacts) * 100 : 0;
  const deployed = agg.ledger.rows.map((r) => ({
    ...r,
    pct: r.allocated ? Math.round(((r.allocated - r.inPlay) / r.allocated) * 100) : 0,
    left: r.inPlay,
  }));
  const lightItems = deployed.filter((r) => r.allocated && r.pct < 45);
  const weatherRows = facts
    .filter((f) => f.weather.length > 0)
    .map((f) => {
      const d = f.weather[0]!;
      return {
        id: f.event.id,
        name: f.event.name,
        tempMax: d.tempMaxC == null ? null : Math.round((d.tempMaxC * 9) / 5 + 32),
        rain: d.precipitationMm != null && d.precipitationMm > 0 ? (d.precipitationMm / 25.4).toFixed(2) : null,
        leadRate: bigOf(leadRateOf(f), '—'),
        days: f.weather.length,
      };
    });

  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-3">
        <SummaryCard
          icon={<CalendarRange className="h-4 w-4" />}
          label={t('measure.range')}
          value={rangeLabel}
        />
        <SummaryCard
          icon={<Boxes className="h-4 w-4" />}
          label={t('measure.events')}
          value={`${events.length}`}
        />
        <SummaryCard
          icon={<Radio className="h-4 w-4" />}
          label={t('measure.live')}
          value={`${liveCount}`}
        />
      </div>

      <ProjectAtlediInsight
        projectName={projectName}
        eventCount={events.length}
        liveEventCount={liveCount}
      />

      {/* Las seis cifras del proyecto, cada una con su viñeta. */}
      {factsQ.isLoading ? (
        <Skeleton className="h-48 w-full" />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <KpiCard
            label={t('projectReport.kpi.events')}
            value={String(agg.events)}
            chip={agg.live ? t('projectReport.kpi.liveNow') : undefined}
            hint={t('projectReport.kpi.eventsHint', { done: agg.done, live: agg.live, upcoming: agg.upcoming })}
          >
            <MiniDots
              dots={captures.map((c) => ({
                title: `${c.name} · ${t(`track.group.${c.phase}`)}`,
                color: c.phase === 'live' ? EVENT_COLORS.live : c.phase === 'upcoming' ? EVENT_COLORS.upcoming : EVENT_COLORS.done,
                h: c.phase === 'upcoming' ? 10 : c.phase === 'live' ? 30 : 22,
              }))}
            />
          </KpiCard>
          <KpiCard label={t('projectReport.kpi.contacts')} value={fmtInt(agg.funnel.contacts)} hint={t('projectReport.kpi.contactsHint')}>
            <MiniBars
              bars={captures.map((c) => ({
                title: `${c.name} · ${fmtInt(c.contacts)}`,
                value: c.contacts,
                color: c.phase === 'live' ? EVENT_COLORS.live : EVENT_COLORS.done,
              }))}
            />
          </KpiCard>
          <KpiCard
            label={t('projectReport.kpi.pace')}
            value={paceGoal ? bigOf(pace, '—') : t('dash.pace.noGoalLabel')}
            hint={
              paceGoal
                ? t('projectReport.kpi.paceHint', { n: fmtInt(doneLeads), goal: fmtInt(paceGoal) })
                : t('projectReport.kpi.paceNoGoal')
            }
          >
            <MiniArc
              parts={[
                { pct: paceGoal ? Math.min(100, (doneLeads / paceGoal) * 100) : 0, color: SEGMENT_COLORS.delivered },
                { pct: paceGoal ? Math.min(100, (liveLeads / paceGoal) * 100) : 0, color: EVENT_COLORS.live },
              ]}
              legend={[
                { label: t('projectReport.kpi.closed'), value: fmtInt(doneLeads), color: SEGMENT_COLORS.delivered },
                { label: t('projectReport.kpi.liveProvisional'), value: fmtInt(liveLeads), color: EVENT_COLORS.live },
                {
                  label: t('projectReport.kpi.remaining'),
                  value: paceGoal ? fmtInt(Math.max(0, paceGoal - doneLeads - liveLeads)) : '—',
                  color: TRACK_COLOR,
                },
              ]}
            />
          </KpiCard>
          <KpiCard label={t('projectReport.kpi.delivered')} value={fmtInt(agg.ledger.delivered)} hint={t('projectReport.kpi.deliveredHint')} valueTone="accent">
            <MiniBars
              bars={captures.map((c) => ({
                title: `${c.name} · ${fmtInt(c.delivered)}`,
                value: c.delivered,
                color: c.phase === 'live' ? EVENT_COLORS.live : SEGMENT_COLORS.delivered,
              }))}
            />
          </KpiCard>
          <KpiCard
            label={t('projectReport.kpi.unaccounted')}
            value={fmtInt(agg.ledger.unaccounted)}
            chip={agg.ledger.unaccounted ? fmtPct(unaccPct) : t('projectReport.kpi.clean')}
            chipTone={agg.ledger.unaccounted ? 'warn' : 'accent'}
            hint={t('projectReport.kpi.unaccountedHint')}
            valueTone={agg.ledger.unaccounted ? 'warn' : 'default'}
          >
            <MiniSplit
              parts={[
                { label: t('projectReport.kpi.accounted'), value: fmtInt(accounted), pct: 100 - unaccPct, color: SEGMENT_COLORS.delivered },
                { label: t('dash.seg.unaccounted'), value: fmtInt(agg.ledger.unaccounted), pct: unaccPct, color: SEGMENT_COLORS.unaccounted },
              ]}
            />
          </KpiCard>
          <KpiCard
            label={t('projectReport.kpi.repeat')}
            value={bigOf(repeatRate, '—')}
            hint={repeatRate.state === 'MEASURED' ? t('dash.basis', { n: uniqueContacts.toLocaleString(), moe: repeatRate.moe ?? 0 }) : t('projectReport.kpi.repeatPending')}
            valueTone="purple"
          >
            <MiniSplit
              parts={[
                { label: t('projectReport.kpi.newPeople'), value: fmtInt(uniqueContacts - repeatContacts), pct: 100 - repeatPct, color: SEGMENT_COLORS.lapsed },
                { label: t('projectReport.kpi.returning'), value: fmtInt(repeatContacts), pct: repeatPct, color: EVENT_COLORS.live },
              ]}
            />
          </KpiCard>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Contactos por captura: una barra por evento, en orden de fecha. */}
        <Card>
          <CardHeader>
            <CardTitle>{t('projectReport.captures.title')}</CardTitle>
            <p className="text-xs text-[var(--color-fg-muted)]">{t('projectReport.captures.note')}</p>
          </CardHeader>
          <CardContent>
            {factsQ.isLoading ? (
              <Skeleton className="h-64 w-full" />
            ) : withData.length === 0 ? (
              <p className="py-8 text-center text-sm text-[var(--color-xk-text-muted)]">{t('projectReport.captures.empty')}</p>
            ) : (
              <div className="h-64 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={captures} margin={{ top: 8, right: 8, bottom: 8, left: -12 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--color-border-subtle)" />
                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: 11, fill: 'var(--color-xk-text-muted)' }}
                      interval={0}
                      angle={captures.length > 4 ? -20 : 0}
                      textAnchor={captures.length > 4 ? 'end' : 'middle'}
                      height={captures.length > 4 ? 60 : 30}
                      tickLine={false}
                    />
                    <YAxis tick={{ fontSize: 11, fill: 'var(--color-xk-text-muted)' }} allowDecimals={false} tickLine={false} axisLine={false} />
                    <Tooltip
                      cursor={{ fill: 'var(--color-bg-elevated)' }}
                      content={({ active, payload }) => {
                        const c = payload?.[0]?.payload as (typeof captures)[number] | undefined;
                        if (!active || !c) return null;
                        return (
                          <div className="rounded-lg border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] px-3 py-2 text-xs shadow-md">
                            <div className="font-bold text-[var(--color-fg-primary)]">{c.name}</div>
                            <div className="text-[var(--color-fg-muted)]">
                              {t('projectReport.captures.tooltip', { contacts: fmtInt(c.contacts), rate: c.leadRate })}
                            </div>
                          </div>
                        );
                      }}
                    />
                    <Bar dataKey="contacts" name={t('projectReport.kpi.contacts')} radius={[4, 4, 0, 0]} isAnimationActive={false}>
                      {captures.map((c) => (
                        <Cell key={c.id} fill={c.phase === 'live' ? EVENT_COLORS.live : c.phase === 'upcoming' ? EVENT_COLORS.upcoming : EVENT_COLORS.done} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Lo desplegado hasta hoy por artículo: total, lo que queda y el %. */}
        <Card>
          <CardHeader>
            <CardTitle>{t('projectReport.deployed.title')}</CardTitle>
            <p className="text-xs text-[var(--color-fg-muted)]">
              {lightItems.length
                ? t('projectReport.deployed.lightNote', { n: lightItems.length })
                : deployed.length
                  ? t('projectReport.deployed.okNote')
                  : t('measure.noStockData')}
            </p>
          </CardHeader>
          <CardContent>
            {factsQ.isLoading ? (
              <Skeleton className="h-64 w-full" />
            ) : (
              <ul className="space-y-3">
                {deployed.map((r) => {
                  const heavy = r.pct >= 70;
                  const light = r.pct < 45;
                  const color = heavy ? SEGMENT_COLORS.delivered : light ? SEGMENT_COLORS.pending : 'var(--color-xk-accent-dim)';
                  return (
                    <li key={r.stockId} className="space-y-1">
                      <div className="flex items-baseline justify-between gap-3 text-sm">
                        <span className="min-w-0 truncate font-semibold text-[var(--color-fg-primary)]">{r.name}</span>
                        <span className="shrink-0 text-[11px] text-[var(--color-fg-subtle)]">
                          {t('projectReport.deployed.basis', { total: fmtInt(r.allocated), left: fmtInt(r.left) })}
                        </span>
                        <span className="w-12 shrink-0 text-right font-mono text-sm font-bold tabular-nums" style={{ color: heavy || light ? color : 'var(--color-fg-primary)' }}>
                          {r.pct}%
                        </span>
                      </div>
                      <div className="h-2 overflow-hidden rounded-full" style={{ background: TRACK_COLOR }}>
                        <div className="h-full rounded-full" style={{ width: `${r.pct}%`, background: color }} />
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      {/* El clima como covariable: al lado de la tasa de leads, no en una nota al pie. */}
      {weatherRows.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{t('projectReport.weather.title')}</CardTitle>
            <p className="text-xs text-[var(--color-fg-muted)]">{t('projectReport.weather.note')}</p>
          </CardHeader>
          <CardContent>
            <ul className="divide-y divide-[var(--color-border-subtle)] text-sm">
              {weatherRows.map((w) => (
                <li key={w.id} className="grid grid-cols-[minmax(0,1fr)_auto_auto_auto] items-center gap-4 py-2">
                  <span className="min-w-0 truncate font-semibold text-[var(--color-fg-primary)]">{w.name}</span>
                  <span className="font-mono text-xs tabular-nums text-[var(--color-fg-muted)]">
                    {w.tempMax != null ? `${w.tempMax}°F` : '—'}
                    {w.days > 1 ? ` · ${t('projectReport.weather.days', { n: w.days })}` : ''}
                  </span>
                  <span className="font-mono text-xs tabular-nums text-[var(--color-fg-muted)]">{w.rain ? t('projectReport.weather.rain', { n: w.rain }) : t('projectReport.weather.dry')}</span>
                  <span className="font-mono text-xs font-bold tabular-nums text-[var(--color-fg-primary)]">{t('projectReport.weather.leadRate', { p: w.leadRate })}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <ProjectThreePanels groupId={groupId} brandId={brandId} events={events} />
      <CapacityIndicator status="pending" />
      {/* A quién llegó el proyecto: las respuestas de las estaciones de todos sus eventos. */}
      <SurveyDemographics eventIds={events.map((e) => e.id)} />
      {single && !factsQ.isLoading && (
        <p className="rounded-xl border border-dashed border-[var(--color-border-default)] p-4 text-sm text-[var(--color-fg-muted)]">
          {t('projectReport.singleNote', { name: projectName })}
        </p>
      )}
    </div>
  );
}

function SummaryCard({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-xl border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] p-4">
      <div className="mb-1 flex items-center gap-1.5 text-[10px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]">
        {icon}
        {label}
      </div>
      <p className="text-lg font-black text-[var(--color-xk-lavender)]" style={{ fontFamily: 'var(--font-display)' }}>
        {value}
      </p>
    </div>
  );
}
